import type {
  SplitShipmentLocationGroup,
  SplitShipmentLocationGroupsView,
} from "@redotech/redo-fulfillment-settings/split-shipment-location-groups";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

interface SplitShipmentLocationPickerProps {
  locations: SplitShipmentLocationGroupsView;
  selected: SplitShipmentLocationGroup[];
  disabled: boolean;
  onChange(groups: SplitShipmentLocationGroup[]): void;
}

export const SplitShipmentLocationPicker = memo(
  function SplitShipmentLocationPicker({
    locations,
    selected,
    disabled,
    onChange,
  }: SplitShipmentLocationPickerProps) {
    const profiles = [
      ...new Set(locations.groups.map((group) => group.profileId)),
    ];
    const missing = selected.filter(
      (selection) =>
        !locations.groups.some(
          (group) =>
            group.profileId === selection.profileId &&
            group.locationGroupId === selection.locationGroupId,
        ),
    );
    return (
      <Flex dir="column" gap="xl">
        {profiles.map((profileId) => {
          const groups = locations.groups.filter(
            (group) => group.profileId === profileId,
          );
          return (
            <Flex dir="column" gap="md" key={profileId}>
              <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
                {groups[0]?.profileName}
              </Text>
              {groups.map((group) => {
                const checked = selected.some(
                  (selection) =>
                    selection.profileId === group.profileId &&
                    selection.locationGroupId === group.locationGroupId,
                );
                return (
                  <RedoCheckbox
                    description={
                      group.unavailableReason ?? group.methodNames.join(", ")
                    }
                    disabled={
                      disabled ||
                      (!checked &&
                        (!!group.unavailableReason || selected.length >= 10))
                    }
                    key={`${profileId}:${group.locationGroupId}`}
                    label={
                      group.locationNames.join(", ") || "Unnamed location group"
                    }
                    setValue={(value) =>
                      onChange(
                        value === true
                          ? [
                              ...selected,
                              {
                                profileId,
                                locationGroupId: group.locationGroupId,
                              },
                            ]
                          : selected.filter(
                              (selection) =>
                                selection.profileId !== profileId ||
                                selection.locationGroupId !==
                                  group.locationGroupId,
                            ),
                      )
                    }
                    size="sm"
                    value={checked}
                  />
                );
              })}
            </Flex>
          );
        })}
        {missing.map((group) => (
          <RedoCheckbox
            description="No longer found in Shopify. Deselect to remove."
            disabled={disabled}
            key={`${group.profileId}:${group.locationGroupId}`}
            label="Unavailable location group"
            setValue={() =>
              onChange(selected.filter((selection) => selection !== group))
            }
            size="sm"
            value
          />
        ))}
        {!locations.groups.length && !missing.length && (
          <Text fontSize="xs" textColor="secondary">
            No location groups found. Add shipping rates in Shopify first.
          </Text>
        )}
      </Flex>
    );
  },
);
